/**
 * MemberClaimMovesDialog - confirm step of a member save that hands them zone
 * pairs with open claims (WPS-160). Lists the claims per (zone, template)
 * pair, with who holds them now, and lets the operator move them or save
 * without moving. Driven by `useClaimMovesConfirm`.
 */

import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import { zoneLabel } from '@/admin/pages/manage/geography/geography-utils'
import type { ClaimMovePreview } from '@/admin/hooks/use-membership-attributes'

interface Props {
  open: boolean
  memberName: string
  preview: ClaimMovePreview | null
  moveClaims: boolean
  onMoveClaimsChange: (value: boolean) => void
  onCancel: () => void
  onConfirm: () => void
}

export function MemberClaimMovesDialog({
  open,
  memberName,
  preview,
  moveClaims,
  onMoveClaimsChange,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useTranslation()
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
    >
      <DialogContent className="max-w-[560px]" data-testid="member-claim-moves-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('members.claimMoves.title', 'Move open claims to {{name}}?', {
              name: memberName,
            })}
          </DialogTitle>
          <DialogDescription>
            {t(
              'members.claimMoves.description',
              '{{count}} open claim(s) are in the zones this save gives {{name}}. They can move to {{name}} now; new claims route to {{name}} either way.',
              { count: preview?.count ?? 0, name: memberName },
            )}
          </DialogDescription>
        </DialogHeader>
        {preview ? (
          <div
            className="max-h-[280px] overflow-y-auto rounded-[8px] border-[0.5px] border-[var(--rule)]"
            data-testid="member-claim-moves-list"
          >
            {preview.pairs.map((pair) => (
              <div
                key={`${pair.channel.key}-${pair.zone.uuid}-${pair.product_template?.uuid ?? 'all'}`}
                className="border-b-[0.5px] border-b-[var(--rule-light)] last:border-b-0"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2 bg-[var(--bg-light)] px-3 py-2">
                  <span className="text-[13px] text-[var(--ink)]">
                    {zoneLabel(pair.zone.name, pair.zone.code)}
                    {pair.product_template ? ` · ${pair.product_template.name}` : ''}
                    <span className="text-[var(--ink-3)]"> · {pair.channel.label}</span>
                  </span>
                  <span className="text-[11px] text-[var(--ink-3)]">
                    {pair.previous_owners.length > 0
                      ? t('members.claimMoves.from', 'From {{names}}', {
                          names: pair.previous_owners.map((o) => o.name).join(', '),
                        })
                      : t('members.claimMoves.unassigned', 'Unassigned')}
                  </span>
                </div>
                {pair.claims.map((claim) => (
                  <div
                    key={claim.uuid}
                    className="flex items-center justify-between gap-3 px-3 py-1.5 text-[13px]"
                  >
                    <span className="text-[var(--ink)]">
                      {claim.number || claim.uuid.slice(0, 8)}
                    </span>
                    <span className="text-[var(--ink-3)]">{claim.state_label}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : null}
        <Label className="flex items-center gap-2 text-[13px] text-[var(--ink)]">
          <Checkbox
            checked={moveClaims}
            onCheckedChange={(value) => onMoveClaimsChange(Boolean(value))}
            data-testid="member-claim-moves-toggle"
          />
          {t('members.claimMoves.moveLabel', 'Move these open claims to {{name}}', {
            name: memberName,
          })}
        </Label>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="button" onClick={onConfirm} data-testid="member-claim-moves-confirm">
            {t('members.claimMoves.confirm', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
